import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, RotateCcw } from 'lucide-react';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

type AspectOption = { label: string; ratio: number | null };

const ASPECT_OPTIONS: AspectOption[] = [
  { label: 'Libre', ratio: null },
  { label: '1:1', ratio: 1 },
  { label: '4:5', ratio: 4 / 5 },
  { label: '9:16', ratio: 9 / 16 },
  { label: '16:9', ratio: 16 / 9 },
  { label: '2:3', ratio: 2 / 3 },
  { label: '3:2', ratio: 3 / 2 },
];

// Hook that encapsulates all crop state and logic
export function useCropState(imageUrl: string, fileName: string, onDone: () => void) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerNodeRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  // Track when the container DOM node is attached (state triggers re-render / effect re-run)
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [selectedAspect, setSelectedAspect] = useState<number>(0);
  const [crop, setCrop] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; crop: CropArea }>({ x: 0, y: 0, crop: { x: 0, y: 0, width: 0, height: 0 } });
  const [displayScale, setDisplayScale] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });

  // Callback ref: when the canvas component mounts, it calls this to register the container
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerNodeRef.current = node;
    setContainerNode(node);
  }, []);

  // Load image
  useEffect(() => {
    setImageLoaded(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
    };
    img.onerror = () => console.error('Failed to load image for cropping');
    img.src = imageUrl;
  }, [imageUrl]);

  const calculateLayout = useCallback(() => {
    if (!imageRef.current || !containerNodeRef.current) return;
    const container = containerNodeRef.current;
    const img = imageRef.current;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    if (cw === 0 || ch === 0) return; // container not yet laid out
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const scale = Math.min(cw / iw, ch / ih, 1);
    const dw = iw * scale;
    const dh = ih * scale;
    const ox = (cw - dw) / 2;
    const oy = (ch - dh) / 2;
    setDisplayScale(scale);
    setImageOffset({ x: ox, y: oy });
    setDisplaySize({ width: dw, height: dh });
    setCrop({ x: 0, y: 0, width: dw, height: dh });
  }, []);

  // Recalculate when image loads or container appears (retry a few frames for layout)
  useEffect(() => {
    if (!imageLoaded || !containerNode) return;
    let cancelled = false;
    let retries = 0;
    const tryLayout = () => {
      if (cancelled) return;
      calculateLayout();
      // Retry up to 5 frames in case container isn't laid out yet
      if (retries < 5 && containerNode.clientWidth === 0) {
        retries++;
        requestAnimationFrame(tryLayout);
      }
    };
    requestAnimationFrame(tryLayout);
    return () => { cancelled = true; };
  }, [imageLoaded, containerNode, calculateLayout]);

  // ResizeObserver: recalculate when container gets actual dimensions
  useEffect(() => {
    if (!containerNode || !imageLoaded) return;
    const ro = new ResizeObserver(() => calculateLayout());
    ro.observe(containerNode);
    return () => ro.disconnect();
  }, [containerNode, imageLoaded, calculateLayout]);

  useEffect(() => {
    const handleResize = () => { if (imageLoaded) calculateLayout(); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [imageLoaded, calculateLayout]);

  // Draw canvas
  useEffect(() => {
    if (!canvasRef.current || !imageRef.current || !imageLoaded) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const container = containerNodeRef.current;
    if (!container) return;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;

    ctx.drawImage(imageRef.current, imageOffset.x, imageOffset.y, displaySize.width, displaySize.height);

    // Dark overlay outside crop
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, 0, canvas.width, imageOffset.y + crop.y);
    ctx.fillRect(0, imageOffset.y + crop.y + crop.height, canvas.width, canvas.height - (imageOffset.y + crop.y + crop.height));
    ctx.fillRect(0, imageOffset.y + crop.y, imageOffset.x + crop.x, crop.height);
    ctx.fillRect(imageOffset.x + crop.x + crop.width, imageOffset.y + crop.y, canvas.width - (imageOffset.x + crop.x + crop.width), crop.height);

    // Crop border
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.strokeRect(imageOffset.x + crop.x, imageOffset.y + crop.y, crop.width, crop.height);

    // Grid lines (rule of thirds)
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 0.5;
    const cx = imageOffset.x + crop.x;
    const cy = imageOffset.y + crop.y;
    for (let i = 1; i <= 2; i++) {
      ctx.beginPath(); ctx.moveTo(cx + (crop.width * i) / 3, cy); ctx.lineTo(cx + (crop.width * i) / 3, cy + crop.height); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx, cy + (crop.height * i) / 3); ctx.lineTo(cx + crop.width, cy + (crop.height * i) / 3); ctx.stroke();
    }

    // Corner handles
    const handleSize = 16;
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 3;
    const corners = [
      { x: cx, y: cy },
      { x: cx + crop.width, y: cy },
      { x: cx, y: cy + crop.height },
      { x: cx + crop.width, y: cy + crop.height },
    ];
    corners.forEach(({ x, y }, idx) => {
      const ddx = idx % 2 === 0 ? 1 : -1;
      const ddy = idx < 2 ? 1 : -1;
      ctx.beginPath(); ctx.moveTo(x, y + ddy * handleSize); ctx.lineTo(x, y); ctx.lineTo(x + ddx * handleSize, y); ctx.stroke();
    });
  }, [crop, imageOffset, displaySize, imageLoaded, containerNode]);

  const getPos = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0]?.clientX ?? 0 : e.clientX;
    const clientY = 'touches' in e ? e.touches[0]?.clientY ?? 0 : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const getHitZone = (pos: { x: number; y: number }): string | null => {
    const cx = imageOffset.x + crop.x;
    const cy = imageOffset.y + crop.y;
    const tolerance = 14;
    const nearLeft = Math.abs(pos.x - cx) < tolerance;
    const nearRight = Math.abs(pos.x - (cx + crop.width)) < tolerance;
    const nearTop = Math.abs(pos.y - cy) < tolerance;
    const nearBottom = Math.abs(pos.y - (cy + crop.height)) < tolerance;
    if (nearTop && nearLeft) return 'nw';
    if (nearTop && nearRight) return 'ne';
    if (nearBottom && nearLeft) return 'sw';
    if (nearBottom && nearRight) return 'se';
    if (nearTop) return 'n';
    if (nearBottom) return 's';
    if (nearLeft) return 'w';
    if (nearRight) return 'e';
    if (pos.x > cx && pos.x < cx + crop.width && pos.y > cy && pos.y < cy + crop.height) return 'move';
    return null;
  };

  const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

  const applyAspectConstraint = (newCrop: CropArea, handle: string): CropArea => {
    const aspectOption = ASPECT_OPTIONS[selectedAspect];
    if (!aspectOption.ratio) return newCrop;
    const ratio = aspectOption.ratio;
    const c = { ...newCrop };
    if (handle === 'move') return c;
    if (handle === 'se' || handle === 'e' || handle === 's') {
      c.height = c.width / ratio;
      if (c.y + c.height > displaySize.height) { c.height = displaySize.height - c.y; c.width = c.height * ratio; }
    } else if (handle === 'sw' || handle === 'w') {
      c.height = c.width / ratio;
      if (c.y + c.height > displaySize.height) { c.height = displaySize.height - c.y; c.width = c.height * ratio; c.x = newCrop.x + newCrop.width - c.width; }
    } else if (handle === 'ne' || handle === 'n') {
      c.width = c.height * ratio;
      if (c.x + c.width > displaySize.width) { c.width = displaySize.width - c.x; c.height = c.width / ratio; c.y = newCrop.y + newCrop.height - c.height; }
    } else if (handle === 'nw') {
      c.height = c.width / ratio;
      if (c.x < 0) { c.x = 0; c.width = newCrop.x + newCrop.width; c.height = c.width / ratio; c.y = newCrop.y + newCrop.height - c.height; }
    }
    return c;
  };

  const handlePointerDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getPos(e);
    const zone = getHitZone(pos);
    if (!zone) return;
    setDragging(zone);
    setDragStart({ x: pos.x, y: pos.y, crop: { ...crop } });
  };

  const handlePointerMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!dragging) return;
    e.preventDefault();
    const pos = getPos(e);
    const dx = pos.x - dragStart.x;
    const dy = pos.y - dragStart.y;
    const sc = dragStart.crop;
    const minSize = 30;
    let newCrop: CropArea;
    if (dragging === 'move') {
      newCrop = { x: clamp(sc.x + dx, 0, displaySize.width - sc.width), y: clamp(sc.y + dy, 0, displaySize.height - sc.height), width: sc.width, height: sc.height };
    } else {
      let x = sc.x, y = sc.y, w = sc.width, h = sc.height;
      if (dragging.includes('e')) { w = clamp(sc.width + dx, minSize, displaySize.width - sc.x); }
      if (dragging.includes('w')) { const newX = clamp(sc.x + dx, 0, sc.x + sc.width - minSize); w = sc.width + (sc.x - newX); x = newX; }
      if (dragging.includes('s')) { h = clamp(sc.height + dy, minSize, displaySize.height - sc.y); }
      if (dragging.includes('n')) { const newY = clamp(sc.y + dy, 0, sc.y + sc.height - minSize); h = sc.height + (sc.y - newY); y = newY; }
      newCrop = { x, y, width: w, height: h };
      newCrop = applyAspectConstraint(newCrop, dragging);
    }
    newCrop.x = clamp(newCrop.x, 0, displaySize.width - minSize);
    newCrop.y = clamp(newCrop.y, 0, displaySize.height - minSize);
    newCrop.width = clamp(newCrop.width, minSize, displaySize.width - newCrop.x);
    newCrop.height = clamp(newCrop.height, minSize, displaySize.height - newCrop.y);
    setCrop(newCrop);
  };

  const handlePointerUp = () => setDragging(null);

  const handleAspectChange = (index: number) => {
    setSelectedAspect(index);
    const option = ASPECT_OPTIONS[index];
    if (!option.ratio) return;
    const ratio = option.ratio;
    let w: number, h: number;
    if (displaySize.width / displaySize.height > ratio) { h = displaySize.height; w = h * ratio; } else { w = displaySize.width; h = w / ratio; }
    setCrop({ x: (displaySize.width - w) / 2, y: (displaySize.height - h) / 2, width: w, height: h });
  };

  const handleReset = () => {
    setSelectedAspect(0);
    setCrop({ x: 0, y: 0, width: displaySize.width, height: displaySize.height });
  };

  const handleCropAndDownload = () => {
    if (!imageRef.current || displayScale === 0) return;
    const sx = crop.x / displayScale;
    const sy = crop.y / displayScale;
    const sw = crop.width / displayScale;
    const sh = crop.height / displayScale;
    const offscreen = document.createElement('canvas');
    offscreen.width = Math.round(sw);
    offscreen.height = Math.round(sh);
    const ctx = offscreen.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(imageRef.current, sx, sy, sw, sh, 0, 0, offscreen.width, offscreen.height);
    offscreen.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      let name = fileName || 'imagen';
      const dotIndex = name.lastIndexOf('.');
      if (dotIndex > 0) name = name.substring(0, dotIndex) + '_recortada' + name.substring(dotIndex);
      else name = name + '_recortada.png';
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onDone();
    }, 'image/png');
  };

  const realWidth = displayScale > 0 ? Math.round(crop.width / displayScale) : 0;
  const realHeight = displayScale > 0 ? Math.round(crop.height / displayScale) : 0;

  return {
    canvasRef, setContainerRef, imageLoaded, selectedAspect, dragging, realWidth, realHeight,
    handlePointerDown, handlePointerMove, handlePointerUp, handleAspectChange, handleReset, handleCropAndDownload,
  };
}

// Canvas component — goes where the expanded image normally is
export const ImageCropCanvas: React.FC<{
  cropState: ReturnType<typeof useCropState>;
}> = ({ cropState }) => {
  const { setContainerRef, canvasRef, imageLoaded, dragging, handlePointerDown, handlePointerMove, handlePointerUp } = cropState;

  return (
    <div ref={setContainerRef} className="w-full h-full relative overflow-hidden touch-none bg-[#F2F2F7]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerUp}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
        style={{ cursor: dragging === 'move' ? 'grabbing' : dragging ? 'crosshair' : 'default' }}
      />
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-brand-primary rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

// Controls component — aspect pills + dimensions + save button
// variant='mobile' replaces the 6 action buttons area
// variant='desktop' goes in the right panel
export const ImageCropControls: React.FC<{
  cropState: ReturnType<typeof useCropState>;
  onCancel: () => void;
  variant: 'mobile' | 'desktop';
}> = ({ cropState, onCancel, variant }) => {
  const { imageLoaded, selectedAspect, realWidth, realHeight, handleAspectChange, handleReset, handleCropAndDownload } = cropState;

  if (variant === 'mobile') {
    return (
      <div className="space-y-2">
        {/* Dimensions */}
        {imageLoaded && (
          <div className="flex items-center justify-center py-1">
            <span className="text-[13px] text-neutral-400">{realWidth} × {realHeight} px</span>
          </div>
        )}
        {/* Aspect ratio pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          {ASPECT_OPTIONS.map((option, i) => (
            <button
              key={option.label}
              type="button"
              onClick={() => handleAspectChange(i)}
              className={`flex-shrink-0 h-8 px-3 rounded-md text-[13px] font-medium transition-colors ${
                selectedAspect === i
                  ? 'bg-brand-primary text-white'
                  : 'bg-surface-card text-neutral-600 border border-neutral-200'
              }`}
            >
              {option.label}
            </button>
          ))}
          <button
            type="button"
            onClick={handleReset}
            className="flex-shrink-0 h-8 w-8 rounded-md bg-surface-card text-neutral-500 border border-neutral-200 flex items-center justify-center transition-colors hover:bg-surface-elevated"
            aria-label="Restablecer"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center justify-center gap-2 h-11 rounded-md text-[15px] font-medium bg-surface-card text-neutral-600 border border-neutral-200 transition-colors hover:bg-surface-elevated"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleCropAndDownload}
            disabled={!imageLoaded}
            className="flex items-center justify-center gap-2 h-11 rounded-md text-[15px] font-medium bg-brand-primary text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-40"
          >
            <Download className="w-4 h-4" />
            Descargar
          </button>
        </div>
      </div>
    );
  }

  // Desktop variant
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex flex-col gap-4 p-4">
        {/* Title */}
        <div>
          <h3 className="text-[17px] font-semibold text-brand-black">Recortar imagen</h3>
          <p className="text-[13px] text-neutral-500 mt-1">Seleccioná el área que quieras recortar</p>
        </div>

        {/* Dimensions */}
        {imageLoaded && (
          <div className="flex items-center justify-center py-2 rounded-md bg-surface-elevated">
            <span className="text-[13px] font-medium text-neutral-500">{realWidth} × {realHeight} px</span>
          </div>
        )}

        {/* Aspect ratio options */}
        <div>
          <p className="text-[13px] font-semibold text-brand-black/50 uppercase tracking-wide mb-2">Tamaño</p>
          <div className="flex flex-wrap gap-1.5">
            {ASPECT_OPTIONS.map((option, i) => (
              <button
                key={option.label}
                type="button"
                onClick={() => handleAspectChange(i)}
                className={`h-8 px-3.5 rounded-md text-[13px] font-medium transition-colors ${
                  selectedAspect === i
                    ? 'bg-brand-primary text-white'
                    : 'bg-surface-card text-neutral-600 border border-neutral-200 hover:bg-surface-elevated'
                }`}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              onClick={handleReset}
              className="h-8 w-8 rounded-md bg-surface-card text-neutral-500 border border-neutral-200 flex items-center justify-center transition-colors hover:bg-surface-elevated"
              aria-label="Restablecer"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom actions */}
      <div className="flex-shrink-0 p-4 pt-3 border-t border-neutral-200 space-y-2">
        <button
          type="button"
          onClick={handleCropAndDownload}
          disabled={!imageLoaded}
          className="w-full flex items-center justify-center gap-2 h-11 rounded-md text-[15px] font-medium bg-brand-primary text-white shadow-sm transition-colors hover:bg-brand-primary-hover disabled:opacity-40"
        >
          <Download className="w-4 h-4" />
          Descargar
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="w-full flex items-center justify-center h-11 rounded-md text-[15px] font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

// Keep default export for backward compat (unused now, but safe)
const ImageCropModal: React.FC<{ imageUrl: string; fileName: string; onClose: () => void }> = () => null;
export default ImageCropModal;
